// Computes the inline `margin` that creates the float-based text-wrap gap
// around the image in the ImageWithText block.
//
// Historically this gap was two hardcoded numbers that depended on float
// direction (8px on the wrap side when the image floats right, i.e.
// textPosition === "left"; 20px otherwise) with a dead ternary branch keyed
// off `<ul>`/`<ol>` markup that produced an identical value either way. Users
// had no control to close this gap, and it was asymmetric between the two
// float directions for no apparent reason (see the block's own
// ImageWithTextSettings.jsx MarginBox, which adjusts *outer* block padding on
// ImageContainer, not this inner wrap gap at all).
//
// `imageGap`, when a finite number, now overrides BOTH the wrap-side gap and
// the bottom gap with a single user-controlled value (wired to a new "Image
// Spacing" slider in the block's Layout tab). When `imageGap` is not a finite
// number (i.e. every block saved before this change), the original
// per-direction defaults are reproduced exactly, so existing saved wheels
// render byte-for-byte unchanged.

export const LEGACY_BOTTOM_GAP_PX = 8;

/** The wrap-side gap default before per-block overrides existed. */
export function getLegacySideGapPx(textPosition) {
  return textPosition === "left" ? 8 : 20;
}

function hasExplicitGap(imageGap) {
  return typeof imageGap === "number" && Number.isFinite(imageGap);
}

/** The gap value actually in effect (user override, or the legacy default). */
export function resolveImageGapPx(textPosition, imageGap) {
  return hasExplicitGap(imageGap)
    ? imageGap
    : getLegacySideGapPx(textPosition);
}

/**
 * Builds the CSS `margin` shorthand for the floated image.
 * @param {"left"|"right"|"top"|"bottom"} textPosition
 * @param {number|undefined} imageGap - user override, or undefined for legacy default
 */
export function computeImageWrapMargin(textPosition, imageGap) {
  const sideGap = resolveImageGapPx(textPosition, imageGap);
  const bottomGap = hasExplicitGap(imageGap) ? imageGap : LEGACY_BOTTOM_GAP_PX;

  // float: right when textPosition === "left" (image floats right, text wraps
  // on the left, so the gap belongs on the left side); float: left otherwise
  // (gap belongs on the right side). Mirrors the `float` expression alongside
  // this margin in index.js.
  return textPosition === "left"
    ? `0 0 ${bottomGap}px ${sideGap}px`
    : `0 ${sideGap}px ${bottomGap}px 0`;
}
